// React
import { memo, useCallback } from "react";

// Servisni sloj
import { MINIMUM_MARGIN_WITHOUT_APPROVAL } from "services/api/pricing/consts";

// UI
import { Badge } from "ui/components/Badge";
import { NumberField } from "ui/components/TextField/NumberField";

// Lokalne komponente
import PricingStepIntro from "ui/modules/pricing/components/PricingStepIntro/PricingStepIntro";

// Tipovi
import { PricingMeteredStepProps } from "./types";

/**
 * Mjereni troškovi — prodajna cijena i naš trošak, s maržom kao izračunatim stupcem.
 *
 * Cijena ispod nabave se ne sprema; to je tvrda brana u modelu, ne upozorenje. Badge je samo
 * da se vidi prije nego što se ode na server.
 */
const PricingMeteredStep: React.FC<PricingMeteredStepProps> = (props: PricingMeteredStepProps) => {
  const { formValues, setFormValues, items } = props;

  const handleValueChange = useCallback(
    (value: number | null, name?: string) => {
      if (!name) return;

      const code = name.replace("value-", "");

      setFormValues((previous) => ({
        ...previous,
        values: { ...previous.values, [code]: value },
      }));
    },
    [setFormValues]
  );

  return (
    <>
      <PricingStepIntro
        title="Mjereni troškovi"
        description={
          <>
            Upišite prodajnu cijenu i naš trošak za svaku stavku koja se naplaćuje po jedinici.
            Marža se računa sama, a <strong>naš trošak se ne prikazuje nigdje izvan ovog
            ekrana</strong>.
          </>
        }
      />

      <div className="p-4 w-full max-w-4xl mx-auto">
        <div className="flex flex-col gap-3">
          {items.map((item) => {
            const price = formValues.values[item.code] ?? 0;
            const cost = formValues.values[item.costKeyCode] ?? 0;
            const margin = price > 0 ? (price - cost) / price : 0;
            const isBelowCost = price <= cost;

            return (
              <div key={item.code} className="flex flex-col md:flex-row gap-3 md:items-end">
                <div className="flex-1">
                  <p className="text-sm text-slate-700">{item.name}</p>
                  <p className="text-xs text-slate-400 font-mono">{item.code}</p>
                </div>

                <div className="w-[160px]">
                  <NumberField
                    name={`value-${item.code}`}
                    label="Prodajna"
                    value={price}
                    onChange={handleValueChange}
                    decimals={4}
                    size="full"
                  />
                </div>

                <div className="w-[160px]">
                  <NumberField
                    name={`value-${item.costKeyCode}`}
                    label="Naš trošak"
                    value={cost}
                    onChange={handleValueChange}
                    decimals={4}
                    size="full"
                  />
                </div>

                <div className="w-[150px] pb-2">
                  {isBelowCost ? (
                    <Badge text="Ispod nabave" variant="danger" height="small" showDot />
                  ) : margin < MINIMUM_MARGIN_WITHOUT_APPROVAL ? (
                    <Badge
                      text={`Marža ${(margin * 100).toFixed(1)} %`}
                      variant="warning"
                      height="small"
                      showDot
                    />
                  ) : (
                    <Badge
                      text={`Marža ${(margin * 100).toFixed(1)} %`}
                      variant="success"
                      height="small"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-500 mt-4">
          Prodajna cijena mora biti veća od našeg troška. Ako je manja ili jednaka, cjenik se
          neće spremiti. To nije upozorenje nego pravilo.
        </p>
      </div>
    </>
  );
};

export default memo(PricingMeteredStep);
